import React from "react";
import { connect } from "react-redux";
import moment from "moment";
import api from "../../../utils/api";
import toastMessage from "../../../utils/toastMessage";
import ReusableDashboard from "../../Dashboard/ReusableDashboard";
import { capitalizeFirstChar, sortDaysOfWeek } from "../RequestDashboard";
import { Modal } from "../../Modal";
import { fetchShippingInQueueData } from "../../../utils/queries/request/shipping-queue";
import language from "../../../language";
import { Table } from "../../Table";
import { generateCSVData } from "../../../utils/formatDataCSV";
import { downloadCSV } from "../../../utils/csvDownloader";
import exportPDF from "../../../utils/exportPDF";
import { defaultChartColors } from "../../Dashboard/DashboardChart";

// eslint-disable-next-line no-unused-vars
const options = {
  colors: ["#AC0207", "#F7B811", "#DDD"],
  chart: {
    title: "Blood Collection",
  },
};

export const periodTypeFilter = {
  type: "select",
  label: "Period type",
  options: [
    { value: "all", label: "All" },
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "weekdays", label: "Week Days" },
  ],
  keyFilter: "periodType",
};
export const statusFilter = {
  type: "select",
  label: "Status",
  options: [
    { value: "pending", label: "Pending" },
    { value: "received", label: "Received" },
  ],
  keyFilter: "status",
};

class ShippingDashboard extends React.Component {
  state = {
    isLoading: false,
    error: {},
    productTypesSuppliedCounts: {},
    suppliedStatusCounts: {},
    filter: {
      startDate: moment().subtract(1, "months").format("YYYY-MM-DD"),
      endDate: moment().format("YYYY-MM-DD"),
      periodType: { value: "weekday", label: "Week day" },
      centers: [],
      centerSites: [],
      status: { value: "pending", label: "Pending" },
    },
    dailyLoading: false,
    weekdayLoading: false,
    monthlyLoading: false,
    requestsLoading: false,
    statsDataLoading: false,
    statsData: {},
    dailyData: [],
    weekdayData: [],
    monthlyData: [],
    page: 1,
    limit: 10,
  };

  componentDidMount = async () => {
    await this.fetchData();
  };

  fetchData = async () => {
    this.setState({
      isLoading: false,
      dailyLoading: true,
      weekdayLoading: true,
      monthlyLoading: true,
      requestsLoading: true,
      statsDataLoading: true,
    });

    const startDate =
      this.state.filter?.startDate ||
      moment().subtract(1, "months").format("YYYY-MM-DD");
    const endDate = this.state.filter?.endDate || moment().format("YYYY-MM-DD");
    const centers = (this.state.filter?.centers || []).map(
      ({ value }) => value
    );
    const centerSites = (this.state.filter?.centerSites || []).map(
      ({ value }) => value
    );

    const base = {
      method: "GET",
      params: { startDate, endDate, centers, centerSites },
    };

    const requestId = Date.now();
    this.latestShippingFetchId = requestId;

    const dailyReq = api({
      ...base,
      url: `/shipping-queue/product-types-supplied-counts`,
      params: {
        ...base.params,
        periodType: "daily",
        status: this.state?.status || "received",
      },
    })
      .then(({ data }) => {
        if (this.latestShippingFetchId !== requestId) return;
        this.setState({ dailyData: data, dailyLoading: false });
      })
      .catch((err) => {
        if (this.latestShippingFetchId !== requestId) return;
        console.error("daily distribution error", err);
        toastMessage("error", err);
        this.setState({ dailyLoading: false });
      });

    const weekdayReq = api({
      ...base,
      url: `/shipping-queue/product-types-supplied-counts`,
      params: {
        ...base.params,
        periodType: "weekday",
        status: this.state?.status || "received",
      },
    })
      .then(({ data }) => {
        if (this.latestShippingFetchId !== requestId) return;
        this.setState({ weekdayData: data, weekdayLoading: false });
      })
      .catch((err) => {
        if (this.latestShippingFetchId !== requestId) return;
        console.error("weekday distribution error", err);
        toastMessage("error", err);
        this.setState({ weekdayLoading: false });
      });

    const monthlyReq = api({
      ...base,
      url: `/shipping-queue/product-types-supplied-counts`,
      params: {
        ...base.params,
        periodType: "monthly",
        status: this.state?.status || "received",
      },
    })
      .then(({ data }) => {
        if (this.latestShippingFetchId !== requestId) return;
        this.setState({ monthlyData: data, monthlyLoading: false });
      })
      .catch((err) => {
        if (this.latestShippingFetchId !== requestId) return;
        console.error("monthly distribution error", err);
        toastMessage("error", err);
        this.setState({ monthlyLoading: false });
      });

    const requestsReq = api({
      ...base,
      url: `/product-request/product-types-requested-counts`,
      params: {
        ...base.params,
        periodType: "monthly",
        status: this.state?.status || "received",
      },
    })
      .then(({ data }) => {
        if (this.latestShippingFetchId !== requestId) return;
        this.setState({ requestsData: data, requestsLoading: false });
      })
      .catch((err) => {
        if (this.latestShippingFetchId !== requestId) return;
        console.error("monthly requests error", err);
        toastMessage("error", err);
        this.setState({ requestsLoading: false });
      });

    const statsReq = api({
      ...base,
      url: `/shipping-queue/shipment-status-counts`,
      params: { ...base.params, status: this.state?.status || "received" },
    })
      .then(({ data }) => {
        if (this.latestShippingFetchId !== requestId) return;
        this.setState({ statsData: data, statsDataLoading: false });
      })
      .catch((err) => {
        if (this.latestShippingFetchId !== requestId) return;
        console.error("shipment-status-counts error", err);
        toastMessage("error", err);
        this.setState({ statsDataLoading: false });
      });

    await Promise.allSettled([
      dailyReq,
      weekdayReq,
      monthlyReq,
      requestsReq,
      statsReq,
    ]);

    if (this.latestShippingFetchId === requestId) {
      this.setState({ isLoading: false });
    }
  };

  onFilterChange(field, value) {
    const _filters = this.state.filter;
    if (field === "date-range") {
      _filters.startDate = value.startDate;
      _filters.endDate = value.endDate;
    } else {
      _filters[field] = value;
    }

    this.setState({
      filter: { ..._filters },
    });
  }

  getData = async (periodType) => {
    try {
      this.setState({ isLoading: true });

      const startDate =
        this.state.filter?.startDate ||
        moment().subtract(1, "months").format("YYYY-MM-DD");
      const endDate =
        this.state.filter?.endDate || moment().format("YYYY-MM-DD");
      const centers = (this.state.filter?.centers || []).map(
        ({ value }) => value
      );
      const centerSites = (this.state.filter?.centerSites || []).map(
        ({ value }) => value
      );

      const options = {
        method: "GET",
        url: `/shipping-queue/product-types-supplied-counts`,
        params: {
          startDate,
          endDate,
          periodType,
          centers,
          centerSites,
          status: this.state?.status || "received", // pending
        },
      };

      const { data } = await api(options);

      this.setState({
        [`${periodType}Data`]: data,
      });

      this.setState({ isLoading: false });

      if (periodType === "monthly") {
        this.setState({ isLoadingRequests: true });
        const options2 = {
          method: "GET",
          url: `/product-request/product-types-requested-counts`,
          params: {
            startDate,
            endDate,
            periodType,
            centers,
            centerSites,
            status: this.state?.status || "received", // pending
          },
        };

        const { data: requestsData } = await api(options2);

        this.setState({
          requestsData,
          isLoadingRequests: false,
        });
      }
    } catch (error) {
      this.setState({ isLoading: false });
      toastMessage("error", error);
    }
  };

  getDataStats = async () => {
    try {
      this.setState({ statsDataLoading: true });

      const startDate =
        this.state.filter?.startDate ||
        moment().subtract(1, "months").format("YYYY-MM-DD");
      const endDate =
        this.state.filter?.endDate || moment().format("YYYY-MM-DD");
      const centers = (this.state.filter?.centers || []).map(
        ({ value }) => value
      );
      const centerSites = (this.state.filter?.centerSites || []).map(
        ({ value }) => value
      );

      const options = {
        method: "GET",
        url: `/shipping-queue/shipment-status-counts`,
        params: {
          startDate,
          endDate,
          centers,
          centerSites,
          status: this.state?.status || "received", // pending
        },
      };

      const { data } = await api(options);

      this.setState({
        statsData: data,
        statsDataLoading: false,
      });
    } catch (error) {
      this.setState({ statsDataLoading: false });
      toastMessage("error", error);
    }
  };

  handleCounts(data) {
    let countsPerProductType = {};
    for (let i = 0; i < data.length; i++) {
      if (data[i].productTypeName) {
        if (!countsPerProductType[data[i].productTypeName]) {
          countsPerProductType[data[i].productTypeName] = {
            ...data[i],
            total: 0,
          };
        }

        countsPerProductType[data[i].productTypeName].total += data[i].count;
      }
    }

    return countsPerProductType;
  }

  capitalizeFirstChar = (value = "") => {
    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  generateChartData = (raw = [], requests = []) => {
    const isRequestMode = Array.isArray(requests) && requests.length > 0;

    // 🟢 New Mode: Combined Request + Distribution Comparison
    if (isRequestMode) {
      const countsMap = new Map();

      const increment = (name, type, count) => {
        if (!name) return;
        if (!countsMap.has(name)) {
          countsMap.set(name, { distribution: 0, requests: 0 });
        }
        countsMap.get(name)[type] += count || 0;
      };

      raw.forEach((item) =>
        increment(
          item.productTypeName || item.componentName,
          "distribution",
          item.count
        )
      );

      requests.forEach((item) =>
        increment(item.productTypeName, "requests", item.count)
      );

      const chartData = [["Product", "Requests", "Distribution"]];
      for (const [name, counts] of countsMap.entries()) {
        chartData.push([name, counts.requests, counts.distribution]);
      }

      return chartData;
    }

    // 🔵 Original Mode: Timeline by Day
    if (
      !raw ||
      (Array.isArray(raw) && raw.length === 0) ||
      (typeof raw === "object" && Object.keys(raw).length === 0)
    ) {
      return null;
    }

    const isArrayFormat = Array.isArray(raw);
    let days = [];
    const allBloodTypes = new Set();

    if (isArrayFormat) {
      const validEntries = raw.filter(
        (item) =>
          item &&
          item.period &&
          (item.productTypeName || item.productName) &&
          typeof item.count !== "undefined"
      );

      if (validEntries.length === 0) return null;

      days = [...new Set(validEntries.map((item) => item.period))].sort(
        (a, b) => new Date(a) - new Date(b)
      );

      validEntries.forEach((item) => {
        let key = item.productTypeName || item.productName;
        if (key) {
          allBloodTypes.add(key);
        }
      });
    } else {
      days = sortDaysOfWeek(Object.keys(raw));
      if (!days || days.length === 0) return null;

      days.forEach((day) => {
        const dayData = raw[day];
        if (dayData && typeof dayData === "object") {
          Object.keys(dayData).forEach((key) => {
            if (key !== "total") {
              allBloodTypes.add(key);
            }
          });
        }
      });
    }

    if (allBloodTypes.size === 0) return null;

    const bloodTypes = Array.from(allBloodTypes).filter(Boolean);
    const chartData = [["Day", ...bloodTypes]];

    days.forEach((day) => {
      const row = [isArrayFormat ? day : this.capitalizeFirstChar(day)];

      bloodTypes.forEach((bt) => {
        if (isArrayFormat) {
          const entry = raw.find(
            (item) =>
              item.period === day &&
              (item.productTypeName || item.productName) === bt
          );
          row.push(entry ? parseInt(entry.count) || 0 : 0);
        } else {
          const dayData = raw[day] || {};
          row.push(parseInt(dayData[bt]) || 0);
        }
      });

      chartData.push(row);
    });

    return chartData;
  };

  handleShowModal(modal, selectedData) {
    this.setState(
      {
        [modal]: true,
        selectedData,
      },
      () => {
        if (modal === "showModalByProduct") {
          this.getDataByProductType("showModalByProduct", {
            product: selectedData.productCode + "",
          });
        } else if (modal === "showModalByStats") {
          this.getDataByProductType("showModalByStats", {
            status: selectedData + "",
          });
        }
      }
    );
  }

  getDataByProductType = async (modal, search = {}) => {
    try {
      const { defaultLanguage } = this.props;
      this.setState({ isLoadingDetails: true });

      const startDate =
        this.state.filter?.startDate ||
        moment().subtract(1, "months").format("YYYY-MM-DD");
      const endDate =
        this.state.filter?.endDate || moment().format("YYYY-MM-DD");
      const centers = (this.state.filter?.centers || []).map(
        ({ value }) => value
      );
      const centerSites = (this.state.filter?.centerSites || []).map(
        ({ value }) => value
      );

      if (modal === "showModalByProduct") {
        const { data, count, totalRecords } = await fetchShippingInQueueData(
          defaultLanguage,
          {
            ...search,
            page: this.state.page,
            limit: this.state.limit,
            startDate,
            endDate,
            centers,
            centerSites,
            status: this.state?.status || "received",
          }
        );

        this.setState({
          detailData: data,
          totalPageCount: count,
        totalRecords,
          isLoadingDetails: false,
        });
      } else if (modal === "showModalByStats") {
        const options = {
          method: "GET",
          url: `/shipping-queue/shipments-by-status`,
          params: {
            page: this.state.page,
            limit: this.state.limit,
            startDate,
            endDate,
            centers,
            centerSites,
            // status: this.state?.status || "received", // pending
            ...search,
          },
        };

        const { data } = await api(options);
        this.setState({
          detailData: data?.data,
          totalPageCount: data?.pagination?.total,
          isLoadingDetails: false,
        });
      }
    } catch (error) {
      this.setState({
        isLoadingDetails: false,
      });
      toastMessage("error", error);
    }
  };

  getDataByProductTypeExport = async (search) => {
    try {
      const { defaultLanguage } = this.props;

      const startDate =
        this.state.filter?.startDate ||
        moment().subtract(1, "months").format("YYYY-MM-DD");
      const endDate =
        this.state.filter?.endDate || moment().format("YYYY-MM-DD");
      const centers = (this.state.filter?.centers || []).map(
        ({ value }) => value
      );
      const centerSites = (this.state.filter?.centerSites || []).map(
        ({ value }) => value
      );

      if (this.state.showModalByProduct) {
        const { data } = await fetchShippingInQueueData(defaultLanguage, {
          ...search,
          page: 1,
          limit: 100000,
          startDate,
          endDate,
          centers,
          centerSites,
          status: this.state?.status || "received",
        });
        return data;
        // this.setState({
        //   detailData: data,
        //   totalPageCount: count,
        //   isLoadingDetails: false,
        // });
      } else if (this.state.showModalByStats) {
        const options = {
          method: "GET",
          url: `/shipping-queue/shipments-by-status`,
          params: {
            page: 1,
            limit: 100000,
            startDate,
            endDate,
            centers,
            centerSites,
            // status: this.state?.status || "received", // pending
            ...search,
          },
        };

        const { data } = await api(options);
        // this.setState({
        //   statusDetailData: data?.data,
        //   totalPageCount: data?.pagination?.total,
        //   isLoadingDetails: false,
        // });
        return data?.data?.data;
      }
    } catch (error) {
      toastMessage("error", error);
      return [];
    }
  };

  handlePagination(page, _tab, limit) {
    this.setState(
      {
        page,
        ...(limit != null && { limit }),
      },
      () => {
        if (this.state.showModalByProduct) {
          this.getDataByProductType("showModalByProduct", {
            product: this.state.selectedData.productCode + "",
          });
        } else if (this.state.showModalByStats) {
          this.getDataByProductType("showModalByStats", {
            status: this.state.selectedData + "",
          });
        }
      }
    );
  }

  handleRemoteSearch = async (search) => {
    await new Promise((r) => this.setState({ searching: true, page: 1 }, r));
    if (this.state.showModalByProduct) {
      await this.getDataByProductType("showModalByProduct", {
        product: this.state.selectedData.productCode + "",
        donationNumber: search || search !== "" ? search : undefined,
      });
    } else if (this.state.showModalByStats) {
      this.getDataByProductType("showModalByStats", {
        status: this.state.selectedData + "",
        ...search,
      });
    }
    this.setState({
      searching: false,
    });
  };

  handleSearch = async (e) => {
    const search = e?.target?.value;
    this.setState({ search_text: search });
  };

  handleCloseModal(modal) {
    this.setState({
      [modal]: false,
    });
  }

  returnTableHeaders(model) {
    if (model === "showModalByProduct") {
      return [
        {
          title: language[this.props.defaultLanguage].donation_number,
          key: "donation.donationNumber",
        },
        {
          title: language[this.props.defaultLanguage].source,
          key: "from.name",
        },
        {
          title: language[this.props.defaultLanguage].destination,
          key: "to.name",
        },
        {
          title: language[this.props.defaultLanguage].blood_group,
          key: "donation.bloodGroup",
        },
        {
          title: language[this.props.defaultLanguage].status,
          key: "status",
          type: "status",
        },
        {
          title:
            language[this.props.defaultLanguage].product_verified_shipped_by,
          key: "addedBy.pin",
          styles: {
            textTransform: "uppercase",
          },
        },
        {
          title: language[this.props.defaultLanguage].date,
          key: "createdAt",
          formatTime: "lll",
          isMoment: true,
        },
      ];
    } else if (model === "showModalByStats") {
      return [
        {
          title: language[this.props.defaultLanguage].donation_number,
          key: "donation.donationNumber",
        },
        {
          title: language[this.props.defaultLanguage].product,
          key: "inventory.name",
        },
        {
          title: language[this.props.defaultLanguage].source,
          key: "from.name",
        },
        {
          title: language[this.props.defaultLanguage].destination,
          key: "to.name",
        },
        {
          title: language[this.props.defaultLanguage].blood_group,
          key: "donation.bloodGroup",
        },
        {
          title: language[this.props.defaultLanguage].status,
          key: "status",
          type: "status",
        },
        {
          title: language[this.props.defaultLanguage].reception_status,
          key: "receptionInfo.status",
          type: "status",
        },
        {
          title: language[this.props.defaultLanguage].reception_date,
          key: "receptionInfo.date",
          formatTime: "lll",
          isMoment: true,
        },
        {
          title:
            language[this.props.defaultLanguage].product_verified_shipped_by,
          key: "addedBy.username",
          styles: {
            textTransform: "uppercase",
          },
        },
        {
          title: language[this.props.defaultLanguage].date,
          key: "createdAt",
          formatTime: "lll",
          isMoment: true,
        },
      ];
    }
    return [];
  }

  downloadExcel = async () => {
    try {
      let model = "",
        title = "";
      let data = [];
      if (!!this.state.showModalByProduct) {
        data = await this.getDataByProductTypeExport({
          product: this.state.selectedData.productCode + "",
        });
        model = "showModalByProduct";
        title = `Blood distribution: ${this.state.selectedData}`;
      } else if (!!this.state.showModalByStats) {
        data = await this.getDataByProductTypeExport({
          status: this.state.selectedData + "",
        });
        model = "showModalByStats";
        title = `Total ${capitalizeFirstChar(
          this.state.selectedData
        )} Blood distribution`;
      }
      const headers = this.returnTableHeaders(model);

      const csvData = generateCSVData(headers, data);

      downloadCSV(csvData, title);
    } catch (error) {}
  };

  downloadPDF = async () => {
    this.setState({
      isLoading3: true,
    });
    let model = "",
      title = "";
    let data = [];
    if (!!this.state.showModalByProduct) {
      data = await this.getDataByProductTypeExport({
        product: this.state.selectedData.productCode + "",
      });
      model = "showModalByProduct";
      title = `Blood distribution: ${this.state.selectedData}`;
    } else if (!!this.state.showModalByStats) {
      data = await this.getDataByProductTypeExport("showModalByStats", {
        status: this.state.selectedData + "",
      });
      model = "showModalByStats";
      title = `Total ${capitalizeFirstChar(
        this.state.selectedData
      )} Blood distribution`;
    }

    const headers = this.returnTableHeaders(model);

    this.setState({
      isLoading3: false,
    });

    exportPDF(title, headers, data || []);
  };

  removeTotalPrefix(text) {
    const prefix = "total";
    if (!text) return "";

    if (text.toLowerCase().startsWith(prefix.toLowerCase())) {
      return text.slice(prefix.length).toLowerCase();
    } else {
      return text.toLowerCase();
    }
  }

  render() {
    const countsPerProductType = this.handleCounts(
      this.state.monthlyData || []
    );
    return (
      <>
        <ReusableDashboard
          loading={this.state.isLoading}
          filtersConfig={{
            hasCenters: true,
            hasSites: true,
            initialValues: this.state.filter,
            onFilterChange: (field, value) => this.onFilterChange(field, value),
            customFilter: [statusFilter],
          }}
          filters={this.state.filter}
          onFind={() => this.fetchData()}
          cardGroups={[
            {
              title: "Request KPIs",
              color: defaultChartColors[2],
              col: 3,
              isLoading:
                this.state.requestsLoading || this.state.monthlyLoading,
              items: (() => {
                const toNum = (v) =>
                  Number.isFinite(Number(v)) ? Number(v) : 0;

                const totalRequests = Array.isArray(this.state.requestsData)
                  ? this.state.requestsData.reduce(
                      (s, it) => s + toNum(it.count),
                      0
                    )
                  : toNum(this.state.requestsData?.total);

                const totalDistributed = Array.isArray(this.state.monthlyData)
                  ? this.state.monthlyData.reduce(
                      (s, it) => s + toNum(it.count),
                      0
                    )
                  : toNum(this.state.monthlyData?.total);

                const fulfillmentRate =
                  totalRequests > 0
                    ? Math.round((totalDistributed / totalRequests) * 100)
                    : 0;

                return [
                  { title: "Requests", count: totalRequests },
                  { title: "Distributed", count: totalDistributed },
                  {
                    title: "Distribution Rate",
                    count: fulfillmentRate,
                  },
                ];
              })(),
            },
            {
              title: "Distribution Stats",
              color: defaultChartColors[0],
              col: 3,
              isLoading: this.state.statsDataLoading,
              items: Object.keys(this.state.statsData || {}).map((key) => ({
                title: capitalizeFirstChar(key),
                count: this.state.statsData[key] || 0,
                onPress:
                  !!this.state.statsData[key] &&
                  (() =>
                    this.handleShowModal(
                      "showModalByStats",
                      this.removeTotalPrefix(key)
                    )),
              })),
            },
            {
              color: defaultChartColors[1],
              title: "Product Distributions",
              col: 3,
              items: Object.keys(countsPerProductType || {}).map((key) => ({
                title: this.capitalizeFirstChar(key),
                count: countsPerProductType[key].total || 0,
                onPress:
                  !!countsPerProductType[key].total &&
                  (() =>
                    this.handleShowModal(
                      "showModalByProduct",
                      countsPerProductType[key]
                    )),
              })),
            },
          ]}
          charts={[
            {
              id: "product-supplied-requests-vs-distributions",
              title: `Requests vs Distributions`,
              type: "ColumnChart",
              data: this.generateChartData(
                this.state.monthlyData,
                this.state.requestsData || []
              ),
              height: "400px",
              col: 12,
            },
            {
              id: "product-supplied-daily-distribution",
              title: `Daily Distribution`,
              type: "Bar",
              data: this.generateChartData(this.state.dailyData),
              height: "400px",
              col: 12,
            },
            {
              id: "product-supplied-weekday-distribution",
              title: `Day of week Distribution`,
              type: "ColumnChart",
              data: this.generateChartData(this.state.weekdayData),
              height: "400px",
              col: 12,
            },
            // {
            //   id: "product-supplied-by-status-details",
            //   title: `Product Supplied By Status`,
            //   type: "ColumnChart",
            //   data: this.generateChartData(this.state.suppliedStatusCounts),
            //   height: "400px",
            //   col: 12,
            // },
          ]}
        />
        <Modal
          show={this.state.showModalByProduct}
          handleClose={() => this.handleCloseModal("showModalByProduct")}
          title={
            this.state.selectedData?.componentName ||
            this.state.selectedData?.productTypeName
          }
          size="lg"
        >
          <div className="card">
            <div className="card-body">
              <Table
                data={this.state.detailData}
                isSearch
                page={this.state.page}
                limit={this.state.limit}
                addSearchButton
                search_text={this.state.search_text}
                searching={this.state.searching}
                handleSearch={(e) => this.onChangeText("search_text", e)}
                handlePressSearch={() =>
                  this.handleRemoteSearch(this.state.search_text)
                }
                totalPageCount={this.state.totalPageCount}
                totalRecords={this.state.totalRecords}
                isLoading={this.state.isLoadingDetails}
                handlePagination={this.handlePagination.bind(this)}
                headers={this.returnTableHeaders("showModalByProduct")}
                filters={[
                  {
                    type: "export",
                    title: language[this.props.defaultLanguage].export_,
                    button_type: "dropdown",
                    icon: "download",
                    options: [
                      {
                        name: "PDF",
                        onPress: this.downloadPDF.bind(this),
                      },
                      {
                        name: "CSV",
                        onPress: this.downloadExcel.bind(this),
                      },
                    ],
                  },
                ]}
              />
            </div>
          </div>
        </Modal>

        <Modal
          show={this.state.showModalByStats}
          handleClose={() => this.handleCloseModal("showModalByStats")}
          title={`Total ${capitalizeFirstChar(
            this.state.selectedData
          )} Requests`}
          size="lg"
        >
          <div className="card">
            <div className="card-body">
              <Table
                data={this.state.detailData}
                isSearch
                page={this.state.page}
                limit={this.state.limit}
                addSearchButton
                search_text={this.state.search_text}
                searching={this.state.searching}
                handleSearch={(e) => this.onChangeText("search_text", e)}
                handlePressSearch={() =>
                  this.handleRemoteSearch(this.state.search_text)
                }
                totalPageCount={this.state.totalPageCount}
                totalRecords={this.state.totalRecords}
                isLoading={this.state.isLoadingDetails}
                handlePagination={this.handlePagination.bind(this)}
                headers={this.returnTableHeaders("showModalByStats")}
                filters={[
                  {
                    type: "export",
                    title: language[this.props.defaultLanguage].export_,
                    button_type: "dropdown",
                    icon: "download",
                    options: [
                      {
                        name: "PDF",
                        onPress: this.downloadPDF.bind(this),
                      },
                      {
                        name: "CSV",
                        onPress: this.downloadExcel.bind(this),
                      },
                    ],
                  },
                ]}
              />
            </div>
          </div>
        </Modal>
      </>
    );
  }
}

const mapStateToProps = (state) => {
  const { defaultLanguage } = state.Language;
  const { filters } = state.Filters;
  return {
    defaultLanguage,
    filters,
  };
};

export default connect(mapStateToProps)(ShippingDashboard);
